// 숙련(마스터리)·초월 강화·숙련 재분배 — Lv99 이후 성장과 엔드게임 골드·강화석 소비처(3개월 루프 C4, 정본 docs/PROGRESSION-720H.md 4절·6절).
// 데이터와 순수 계산만 둔다(three·DOM·저장소 import 금지). 저장은 장비 장부(gearLedger GearState.ms)가 한다 — 골드·강화석을 쓰는 연산과
// 같은 장부라야 "골드는 빠졌는데 배분은 안 바뀜" 같은 반쪽 결과가 없다(연산 id 멱등·탭 합치기도 장부 규칙 그대로).
// 숙련 EXP는 따로 저장하지 않는다: 성장 장부(영웅·회차별 growth 원장)의 합계가 Lv99 누적 EXP(XP_MAX)를 넘은 몫의 합이 숙련 EXP다.
// 그래서 숙련 레벨은 원장에서 항상 같은 값으로 다시 계산되고, 이관·되돌리기가 필요 없다.
import { XP_TO_NEXT } from './xpTable';
import { LEVEL_MAX, XP_MAX } from './growth';
import type { EquipSlot } from './content/types';

// ── 숙련 레벨 ──────────────────────────────────────
/**
 * 숙련 m → m+1 EXP = Lv98→99 EXP × (base + step·m), 최대 max(PROGRESSION-720H 4절 데이터 계약, tools/sim/pacing720이 같은 값을 쓴다).
 * 하루 8h 시뮬에서 숙련 1 = 596.7h, 90일 끝 숙련 32(시드 1).
 */
export const MASTERY = { base: 0.6, step: 0.012, max: 300 } as const;
export function masteryXpToNext(m: number, table: readonly number[] = XP_TO_NEXT): number {
  return Math.round(table[LEVEL_MAX - 1] * (MASTERY.base + MASTERY.step * m));
}
/** 숙련 m에 닿는 누적 숙련 EXP(인덱스 0..max) */
export const MASTERY_XP_AT: readonly number[] = (() => {
  const out = [0];
  for (let m = 0; m < MASTERY.max; m++) out.push(out[m] + masteryXpToNext(m));
  return out;
})();
/** 성장 원장 하나의 EXP 합계 → 숙련에 들어가는 몫(Lv99 누적 EXP를 넘은 것) */
export function overflowXp(total: number): number {
  return Number.isFinite(total) && total > XP_MAX ? total - XP_MAX : 0;
}
/** 누적 숙련 EXP → 숙련 레벨(0..max) */
export function masteryLevelOf(xp: number): number {
  if (!(xp > 0)) return 0;
  let lo = 0, hi = MASTERY.max;
  while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (MASTERY_XP_AT[mid] <= xp) lo = mid; else hi = mid - 1; }
  return lo;
}
/** 진행 표시: 숙련 레벨·이번 레벨 안에서 쌓은 EXP·필요 EXP(최대면 need 0) */
export function masteryProgress(xp: number): { level: number; into: number; need: number } {
  const level = masteryLevelOf(xp);
  if (level >= MASTERY.max) return { level, into: 0, need: 0 };
  return { level, into: Math.max(0, Math.floor(xp - MASTERY_XP_AT[level])), need: MASTERY_XP_AT[level + 1] - MASTERY_XP_AT[level] };
}

// ── 숙련 포인트 ────────────────────────────────────
export type MasteryStat = 'atk' | 'hp' | 'crit' | 'gold';
export const MASTERY_STATS: readonly MasteryStat[] = ['atk', 'hp', 'crit', 'gold'];
/** 저장 순서 = MASTERY_STATS 순서 [공격, HP, 치명, 골드] */
export type MasteryAlloc = readonly [number, number, number, number];
export const EMPTY_MASTERY: MasteryAlloc = [0, 0, 0, 0];
/** 포인트 1점 효과(4절 계약): 공격 +0.5%, HP +1%, 치명 +0.1%p, 골드 획득 +1% */
export const MASTERY_EFFECT: Readonly<Record<MasteryStat, number>> = { atk: 0.005, hp: 0.01, crit: 0.001, gold: 0.01 };
/** 한 능력치에 넣을 수 있는 최대 포인트. 4 × 75 = 300 = 숙련 최대라 남는 포인트가 없고, 한 곳 몰빵 상한은 공격 +37.5% */
export const MASTERY_STAT_CAP = 75;
export const MASTERY_NAME: Readonly<Record<MasteryStat, string>> = { atk: '공격', hp: '생명력', crit: '치명', gold: '재물' };
/** 숙련 레벨 → 포인트(1레벨 = 1포인트) */
export function masteryPoints(level: number): number { return Math.max(0, Math.min(MASTERY.max, Math.floor(level))); }
export function masterySum(a: MasteryAlloc): number { return a[0] + a[1] + a[2] + a[3]; }
const okInt = (v: unknown, max: number): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 && v <= max;
/** 형식(정수 4칸·칸 상한)만 본다. 포인트 검사는 masteryAllocValid */
export function masteryAllocShape(a: unknown): a is MasteryAlloc {
  return Array.isArray(a) && a.length === 4 && a.every((v) => okInt(v, MASTERY_STAT_CAP));
}
export function masteryAllocValid(a: unknown, points: number): a is MasteryAlloc {
  return masteryAllocShape(a) && masterySum(a) <= points;
}
/** 한 칸이라도 줄면 재분배(골드). 늘리기만 하는 배분은 무료 */
export function isRespec(from: MasteryAlloc, to: MasteryAlloc): boolean {
  for (let i = 0; i < 4; i++) if (to[i] < from[i]) return true;
  return false;
}
/** 재분배 비용: base × growth^min(횟수, maxSteps), 1만 단위 반올림. 0회 100만 → 10회째부터 약 5,770만(6절 싱크 4) */
export const RESPEC_GOLD = { base: 1_000_000, growth: 1.5, maxSteps: 10 } as const;
export function respecCost(count: number): number {
  const n = Math.max(0, Math.min(RESPEC_GOLD.maxSteps, Math.floor(count)));
  return Math.round((RESPEC_GOLD.base * Math.pow(RESPEC_GOLD.growth, n)) / 10_000) * 10_000;
}
/** 포인트가 줄어든 경우(다른 기기 원장 미동기 등) 효과에 쓸 배분: 넘치면 비례로 줄인다(저장은 그대로, statAlloc fitStats와 같은 규칙) */
export function fitMastery(a: MasteryAlloc, points: number): MasteryAlloc {
  const sum = masterySum(a);
  if (sum <= points) return a;
  const k = points > 0 ? points / sum : 0;
  return [Math.floor(a[0] * k), Math.floor(a[1] * k), Math.floor(a[2] * k), Math.floor(a[3] * k)];
}

// ── 초월 강화(+16~+20, 부위 단위) ──────────────────────
/**
 * 6부위 각각에 쌓는 초월 단계(0..5 = +15 위 +16..+20). 장비가 아니라 부위에 붙는다: 장비를 바꿔도 단계는 남고, 그 부위 장비가 +15일 때만 효과가 난다.
 * 실패해도 단계가 내려가지 않는다(소모만). 천장: 그 단계 실패마다 확률 +20%(곱), 최대 3배 — 강화 천장(ENHANCE_PITY)과 같은 규칙. 확률은 화면에 공개한다.
 * 비용은 최상 강화석(모자라면 아래 등급을 5:1 변환) + 골드. 해금: +16·17 = 1차 환생, +18 = 2차 환생, +19·20 = 2차 환생 + 숙련 10·20.
 */
export const TRANSCEND_MAX = 5;
export interface TranscendStep { readonly to: number; readonly chance: number; readonly stones: number; readonly gold: number; readonly gate: { readonly cycle?: number; readonly mastery?: number } }
export const TRANSCEND_STEPS: readonly TranscendStep[] = [
  { to: 16, chance: 0.5, stones: 120, gold: 2_000_000, gate: { cycle: 1 } },
  { to: 17, chance: 0.4, stones: 160, gold: 3_000_000, gate: { cycle: 1 } },
  { to: 18, chance: 0.3, stones: 200, gold: 4_500_000, gate: { cycle: 2 } },
  { to: 19, chance: 0.25, stones: 240, gold: 3_500_000, gate: { cycle: 2, mastery: 10 } },
  { to: 20, chance: 0.2, stones: 300, gold: 4_500_000, gate: { cycle: 2, mastery: 20 } },
];
export const TRANSCEND_PITY = { step: 0.2, max: 3 } as const;
/** 초월 강화석 등급(최상) */
export const TRANSCEND_STONE_TIER = 3;
/** 부위별 효과: 공격 부위(무기·장갑·목걸이) 단계당 공격 +2%, 방어 부위(투구·갑옷·신발) 단계당 HP +3% */
export const TRANSCEND_EFFECT = { atkPerStep: 0.02, hpPerStep: 0.03 } as const;
/** SLOT_ORDER(weapon, helm, armor, gloves, boots, amulet)와 같은 순서의 효과 종류 */
export const TRANSCEND_SLOT_KIND: readonly ('atk' | 'hp')[] = ['atk', 'hp', 'hp', 'atk', 'hp', 'atk'];
/** 지금 열린 초월 최대 단계(0..5) */
export function transcendUnlocked(cycle: number, mastery: number): number {
  let n = 0;
  for (const s of TRANSCEND_STEPS) {
    if (s.gate.cycle != null && cycle < s.gate.cycle) break;
    if (s.gate.mastery != null && mastery < s.gate.mastery) break;
    n++;
  }
  return n;
}
/** 단계 step(0..4, 다음에 올릴 단계)의 실효 확률(천장 포함) */
export function transcendChance(step: number, fails: number): { base: number; mult: number; chance: number } {
  const s = TRANSCEND_STEPS[Math.max(0, Math.min(TRANSCEND_MAX - 1, step))];
  const mult = Math.min(TRANSCEND_PITY.max, 1 + TRANSCEND_PITY.step * Math.max(0, fails));
  return { base: s.chance, mult, chance: Math.min(1, s.chance * mult) };
}
/** 천장 포함 한 단계 기대 시도 수 */
export function transcendExpectedTries(step: number): number {
  let reach = 1, e = 0;
  for (let f = 0; f < 200 && reach > 1e-9; f++) {
    const c = transcendChance(step, f).chance;
    e += reach;
    reach *= 1 - c;
  }
  return e;
}

// ── 숙련 이정표 ────────────────────────────────────
/** 이정표 보상(받기 = 장부 연산 1회, 인덱스 비트로 한 번만). stones = 최상 강화석, protects = 보호 주문서 */
export interface MasteryMilestone { readonly at: number; readonly stones: number; readonly protects: number; readonly gold: number; readonly note?: string }
export const MASTERY_MILESTONES: readonly MasteryMilestone[] = [
  { at: 1, stones: 50, protects: 2, gold: 1_000_000, note: '숙련 개방' },
  { at: 5, stones: 80, protects: 2, gold: 1_500_000 },
  { at: 10, stones: 120, protects: 3, gold: 2_000_000, note: '초월 +19 해금' },
  { at: 15, stones: 150, protects: 3, gold: 2_500_000 },
  { at: 20, stones: 200, protects: 4, gold: 3_000_000, note: '초월 +20 해금' },
  { at: 25, stones: 250, protects: 4, gold: 3_500_000 },
  { at: 30, stones: 300, protects: 5, gold: 4_000_000 },
  { at: 40, stones: 350, protects: 5, gold: 5_000_000 },
  { at: 50, stones: 400, protects: 6, gold: 6_000_000 },
  { at: 75, stones: 500, protects: 6, gold: 8_000_000 },
  { at: 100, stones: 600, protects: 8, gold: 10_000_000 },
  { at: 150, stones: 800, protects: 8, gold: 14_000_000 },
  { at: 200, stones: 1000, protects: 10, gold: 18_000_000 },
  { at: 250, stones: 1200, protects: 10, gold: 22_000_000 },
  { at: 300, stones: 1500, protects: 12, gold: 30_000_000, note: '숙련 완성' },
];
/** 받은 이정표 비트마스크의 유효 범위(이정표 수만큼 비트) */
export const MILESTONE_MASK_MAX = 2 ** MASTERY_MILESTONES.length - 1;
/** 숙련 level에서 받을 수 있고 아직 안 받은 이정표 인덱스 */
export function claimableMilestones(level: number, claimed: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < MASTERY_MILESTONES.length; i++) if (MASTERY_MILESTONES[i].at <= level && !(Math.floor(claimed / 2 ** i) % 2)) out.push(i);
  return out;
}
export function milestoneClaimed(claimed: number, i: number): boolean { return Math.floor(claimed / 2 ** i) % 2 === 1; }

// ── 전투 보정 ──────────────────────────────────────
/** 숙련 배분 + 초월 단계 → 공격%·HP%·치명·골드% (deriveStats rebirth 입력에 더한다). slotActive[i] = 그 부위 장비가 +15 */
export interface MasteryStatsOut { atkPct: number; hpPct: number; crit: number; goldPct: number }
export function makeMasteryStats(): MasteryStatsOut { return { atkPct: 0, hpPct: 0, crit: 0, goldPct: 0 }; }
export function masteryStats(out: MasteryStatsOut, a: MasteryAlloc, tr: readonly number[], slotActive: readonly boolean[]): MasteryStatsOut {
  out.atkPct = a[0] * MASTERY_EFFECT.atk; out.hpPct = a[1] * MASTERY_EFFECT.hp; out.crit = a[2] * MASTERY_EFFECT.crit; out.goldPct = a[3] * MASTERY_EFFECT.gold;
  for (let i = 0; i < TRANSCEND_SLOT_KIND.length; i++) {
    const n = slotActive[i] ? Math.max(0, Math.min(TRANSCEND_MAX, tr[i] ?? 0)) : 0;
    if (TRANSCEND_SLOT_KIND[i] === 'atk') out.atkPct += n * TRANSCEND_EFFECT.atkPerStep; else out.hpPct += n * TRANSCEND_EFFECT.hpPerStep;
  }
  return out;
}

// ── 화면 보기(game/mastery.ts가 채우고 UI가 읽는다. 타입만) ──
/** 화면용 보기(UI가 읽는다). 같은 객체를 재사용하고 revision으로 바뀜을 안다 */
export interface MasterySlotView { readonly slot: EquipSlot; readonly step: number; readonly fails: number; readonly active: boolean; readonly chance: number; readonly stones: number; readonly gold: number }
export interface MasteryLastView { seq: number; kind: 'alloc' | 'transcend' | 'claim'; code: string; slot: EquipSlot | ''; ok: boolean; from: number; to: number; chance: number; gold: number }
export interface MasteryView {
  readonly revision: number;
  readonly level: number;
  readonly into: number;
  readonly need: number;
  readonly points: number;
  readonly alloc: MasteryAlloc;
  readonly free: number;
  readonly respecs: number;
  readonly respecCost: number;
  /** 지금 열린 초월 최대 단계(0..5) */
  readonly unlock: number;
  readonly slots: readonly MasterySlotView[];
  /** 받을 수 있는 이정표 인덱스 */
  readonly claimable: readonly number[];
  readonly claimed: number;
  readonly last: Readonly<MasteryLastView>;
}


// ── 저장 형식(장비 장부 GearState.ms) ─────────────────
/** a = 배분, r = 재분배 횟수, tr = 부위별 초월 단계, tf = 부위별 다음 단계 실패 수(천장), cl = 받은 이정표 비트 */
export interface MasterySave { a: [number, number, number, number]; r: number; tr: number[]; tf: number[]; cl: number }
export function freshMastery(): MasterySave { return { a: [0, 0, 0, 0], r: 0, tr: [0, 0, 0, 0, 0, 0], tf: [0, 0, 0, 0, 0, 0], cl: 0 }; }
export function masteryEmpty(m: MasterySave): boolean {
  return masterySum(m.a) === 0 && m.r === 0 && m.cl === 0 && m.tr.every((v) => v === 0) && m.tf.every((v) => v === 0);
}
/** 저장 원문(JSON 값) → 검증된 값. 틀리면 null(장부가 corrupt로 다룬다) */
export function parseMastery(v: unknown): MasterySave | null {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  if (!masteryAllocShape(o.a) || !okInt(o.r, 1_000_000) || !okInt(o.cl, MILESTONE_MASK_MAX)) return null;
  if (!Array.isArray(o.tr) || o.tr.length !== 6 || !o.tr.every((x) => okInt(x, TRANSCEND_MAX))) return null;
  if (!Array.isArray(o.tf) || o.tf.length !== 6 || !o.tf.every((x) => okInt(x, 100_000))) return null;
  return { a: [o.a[0], o.a[1], o.a[2], o.a[3]], r: o.r, tr: (o.tr as number[]).slice(), tf: (o.tf as number[]).slice(), cl: o.cl };
}
export function cloneMastery(m: MasterySave): MasterySave { return { a: [m.a[0], m.a[1], m.a[2], m.a[3]], r: m.r, tr: m.tr.slice(), tf: m.tf.slice(), cl: m.cl }; }
